import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FieldError } from '@/components/common/Primitives';
import { STATES } from '@/data/states';
import { BUSINESS_TYPES } from '@/data/taxonomy';
import { citiesOfState } from '@/lib/geo';
import { useData } from '@/hooks/useData';
import { formatCnpj, formatPhone, onlyDigits } from '@/lib/format';
import { LogoUpload } from './LogoUpload';

export const OTHER_CITY = '__other';

export const Field = ({ id, label, hint, error, required, children, className }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-sm">{label}{required && <span className="ml-0.5 text-primary">*</span>}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && !error && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    <FieldError msg={error} id={`${id}-error`} />
  </div>
);

export const SelectField = ({ id, value, onChange, options, placeholder, invalid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger id={id} className="border-white/10 bg-[#0b0f16]" aria-invalid={invalid} data-testid={id}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-72 border-white/10 bg-[#0b0f16]">
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const emptyCompany = { logoUrl: '', name: '', tagline: '', description: '', categoryId: '', businessType: '', uf: '', city: '', cityOther: '', cnpj: '', website: '', whatsapp: '', email: '', linkedin: '', instagram: '', facebook: '' };

export const companyToForm = (c) => {
  const known = citiesOfState(c.location.uf).includes(c.location.city);
  return {
    ...emptyCompany,
    logoUrl: c.logoUrl || '',
    name: c.name, tagline: c.tagline || '', description: c.description || '', categoryId: c.categoryId, businessType: c.businessType || '',
    uf: c.location.uf, city: known ? c.location.city : OTHER_CITY, cityOther: known ? '' : c.location.city,
    cnpj: c.cnpj || '', website: c.website || '', whatsapp: c.whatsapp || '', email: c.email || '',
    linkedin: c.socials?.linkedin || '', instagram: c.socials?.instagram || '', facebook: c.socials?.facebook || '',
  };
};

export const formToCompany = (f) => ({
  logoUrl: f.logoUrl || null,
  name: f.name.trim(),
  tagline: f.tagline.trim(),
  description: f.description.trim(),
  categoryId: f.categoryId,
  businessType: f.businessType,
  location: { uf: f.uf, city: f.city === OTHER_CITY ? f.cityOther.trim() : f.city },
  cnpj: f.cnpj ? formatCnpj(f.cnpj) : null,
  website: f.website ? (f.website.startsWith('http') ? f.website.trim() : `https://${f.website.trim()}`) : null,
  whatsapp: f.whatsapp ? formatPhone(f.whatsapp) : null,
  email: f.email.trim() || null,
  socials: { linkedin: f.linkedin.trim(), instagram: f.instagram.trim().replace(/^@/, ''), facebook: f.facebook.trim() },
});

export function validateCompany(f) {
  const e = {};
  if (f.name.trim().length < 3) e.name = 'Informe o nome da empresa (mínimo 3 caracteres).';
  if (f.tagline.trim().length < 10) e.tagline = 'Descreva em uma frase o que a empresa faz (mínimo 10 caracteres).';
  if (f.description.trim().length < 40) e.description = 'A descrição precisa ter ao menos 40 caracteres.';
  if (!f.categoryId) e.categoryId = 'Selecione a categoria principal.';
  if (!f.businessType) e.businessType = 'Selecione o tipo de negócio.';
  if (!f.uf) e.uf = 'Selecione o estado.';
  if (!f.city) e.city = 'Selecione a cidade.';
  if (f.city === OTHER_CITY && f.cityOther.trim().length < 2) e.cityOther = 'Informe o nome da cidade.';
  if (f.cnpj && onlyDigits(f.cnpj).length !== 14) e.cnpj = 'CNPJ deve ter 14 dígitos (ou deixe em branco).';
  if (f.website && !/^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#].*)?$/i.test(f.website.trim())) e.website = 'Informe um endereço de site válido.';
  if (f.whatsapp && ![10, 11].includes(onlyDigits(f.whatsapp).length)) e.whatsapp = 'Informe DDD + número (10 ou 11 dígitos).';
  if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) e.email = 'E-mail comercial inválido.';
  return e;
}

/** Campos do perfil empresarial (usado no cadastro e na edição). */
export const CompanyFields = ({ values, errors = {}, onChange }) => {
  const { categories } = useData();
  const set = (k) => (v) => onChange({ ...values, [k]: typeof v === 'string' ? v : v.target.value, ...(k === 'uf' ? { city: '', cityOther: '' } : {}) });
  const cities = useMemo(() => (values.uf ? citiesOfState(values.uf) : []), [values.uf]);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <LogoUpload value={values.logoUrl} onChange={(url) => onChange({ ...values, logoUrl: url })} />
      </div>
      <Field id="company-name-input" label="Nome da empresa" required error={errors.name} className="sm:col-span-2">
        <Input id="company-name-input" value={values.name} onChange={set('name')} maxLength={80} aria-invalid={!!errors.name} data-testid="company-name-input" />
      </Field>
      <Field id="company-tagline-input" label="Frase de apresentação" required error={errors.tagline} hint="Aparece nos cartões do diretório." className="sm:col-span-2">
        <Input id="company-tagline-input" value={values.tagline} onChange={set('tagline')} maxLength={110} aria-invalid={!!errors.tagline} data-testid="company-tagline-input" />
      </Field>
      <Field id="company-category-select" label="Categoria" required error={errors.categoryId}>
        <SelectField id="company-category-select" value={values.categoryId} onChange={set('categoryId')} placeholder="Selecione" invalid={!!errors.categoryId} options={categories.filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }))} />
      </Field>
      <Field id="company-business-type-select" label="Tipo de negócio" required error={errors.businessType}>
        <SelectField id="company-business-type-select" value={values.businessType} onChange={set('businessType')} placeholder="Selecione" invalid={!!errors.businessType} options={BUSINESS_TYPES.map((b) => ({ value: b.id, label: b.name }))} />
      </Field>
      <Field id="company-state-select" label="Estado" required error={errors.uf}>
        <SelectField id="company-state-select" value={values.uf} onChange={set('uf')} placeholder="Selecione" invalid={!!errors.uf} options={STATES.map((s) => ({ value: s.uf, label: `${s.name} (${s.uf})` }))} />
      </Field>
      <Field id="company-city-select" label="Cidade" required error={errors.city} hint={values.uf ? 'Cidades fora da lista aparecem no centro do estado no mapa.' : 'Selecione o estado primeiro.'}>
        <SelectField id="company-city-select" value={values.city} onChange={set('city')} placeholder={values.uf ? 'Selecione' : '—'} invalid={!!errors.city} options={[...cities.map((c) => ({ value: c, label: c })), ...(values.uf ? [{ value: OTHER_CITY, label: 'Outra cidade...' }] : [])]} />
      </Field>
      {values.city === OTHER_CITY && (
        <Field id="company-city-other-input" label="Nome da cidade" required error={errors.cityOther} className="sm:col-span-2">
          <Input id="company-city-other-input" value={values.cityOther} onChange={set('cityOther')} maxLength={60} data-testid="company-city-other-input" />
        </Field>
      )}
      <Field id="company-description-input" label="Descrição" required error={errors.description} hint={`${values.description.length}/1200`} className="sm:col-span-2">
        <Textarea id="company-description-input" rows={5} value={values.description} onChange={set('description')} maxLength={1200} aria-invalid={!!errors.description} data-testid="company-description-input" />
      </Field>
      <Field id="company-cnpj-input" label="CNPJ (opcional)" error={errors.cnpj} hint="Na demonstração o CNPJ não é verificado. Suporte futuro à verificação.">
        <Input id="company-cnpj-input" inputMode="numeric" value={formatCnpj(values.cnpj)} onChange={(e) => onChange({ ...values, cnpj: onlyDigits(e.target.value) })} placeholder="00.000.000/0000-00" data-testid="company-cnpj-input" />
      </Field>
      <Field id="company-website-input" label="Site (opcional)" error={errors.website}>
        <Input id="company-website-input" value={values.website} onChange={set('website')} placeholder="www.suaempresa.com.br" data-testid="company-website-input" />
      </Field>
      <Field id="company-whatsapp-input" label="WhatsApp comercial (opcional)" error={errors.whatsapp} hint="Será exibido publicamente no perfil.">
        <Input id="company-whatsapp-input" inputMode="tel" value={formatPhone(values.whatsapp)} onChange={(e) => onChange({ ...values, whatsapp: onlyDigits(e.target.value) })} placeholder="(00) 00000-0000" data-testid="company-whatsapp-input" />
      </Field>
      <Field id="company-email-input" label="E-mail comercial (opcional)" error={errors.email}>
        <Input id="company-email-input" type="email" value={values.email} onChange={set('email')} data-testid="company-email-input" />
      </Field>
      <Field id="company-linkedin-input" label="LinkedIn (opcional)"><Input id="company-linkedin-input" value={values.linkedin} onChange={set('linkedin')} placeholder="URL ou nome da página" /></Field>
      <Field id="company-instagram-input" label="Instagram (opcional)"><Input id="company-instagram-input" value={values.instagram} onChange={set('instagram')} placeholder="@suaempresa" /></Field>
    </div>
  );
};